import React, { useState, useEffect, memo, useRef } from 'react';
import { UncontrolledTooltip } from 'reactstrap';
import { connect } from 'react-redux';

import {
  DEALERPROFILE_PAGE_PATH,
  INVENTORY_DETAILS_PAGE_PATH,
  INVENTORY_PAGE_PATH,
  VIN_STATUS_TRACKER
} from '../../constants/appPathConst';
import CustomSelect from '../../components/GeneralForm/CustomSelect';
import {
  handleDataFilter,
  handlePreSavedId,
  retriveDataFilterStorage
} from '../../containers/Dashboard/dashboardSlice';
import { CLR_TEXT_ACTIVE, CLR_TEXT_INACTIVE } from '../../constants/colorConst';
import { STORAGE_LASTACTIVEINDEX, STORAGE_LASTINACTIVEINDEX } from '../../constants/storageConst';
import { cancelPreviousPageRequests } from '../../services/Api';
import { hasActionPermission, retrieveDealerDetails } from '../../generalUtils/rbacUtils';
import { MODULE_APP_GLOBAL } from '../../constants/moduleSlugConst';
import UserService from '../../services/userService';
import { ACTION_ACTIVE_DEALER_IN_DD_LIST } from '../../constants/actionSlugConst';

const DealershipsDropdown = props => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [filteredDealers, setFilteredDealers] = useState([]);
  const [active, setActive] = useState(true);

  const {
    allDealerList,
    activeDealersList,
    inActiveDealersList,
    fetchMyAllDealersReq,
    user,
    dealer,
    handleLayoutSetDealerInfo,
    handleLayoutSetSessionDealerInfo,
    location,
    isLoading,
    navigate,
    handleDataFilter,
    handlePreSavedId,
    themeArr,
    handleLayoutSetUserInfo
  } = props;

  const userIsDirectlogin = user?.isdirectlogin;
  const sessionDealerVal = retrieveDealerDetails(true);
  const dealerValue = userIsDirectlogin
    ? sessionDealerVal?.user_id || sessionDealerVal?.value
    : dealer?.value;
  const { themeColorObj, isLightTheme } = themeArr;

  useEffect(() => {
    if (dealerValue) {
      handlePreSavedId(null);
      const dataFilter = retriveDataFilterStorage() ?? [dealerValue];
      if (dataFilter?.length === 1) {
        handleDataFilter([dealerValue]);
      }
    }
  }, [dealerValue]);

  useEffect(() => {
    if (user?.user_role_slug) {
      fetchMyAllDealersReq();
    }
  }, [user?.user_role_slug]);

  useEffect(() => {
    const getUserRolePermissions = async () => {
      const res = await UserService.getUserRolePermissions();
      if (res.status) {
        const [permissions] = res.data;
        const { user_module_permissiondata } = permissions;
        handleLayoutSetUserInfo({ ...user, user_module_permissiondata });
      }
    };

    if (user?.user_id && !userIsDirectlogin) {
      getUserRolePermissions();
    }
  }, [user?.user_id, userIsDirectlogin]);

  // active / inactive
  const hasSyncedRef = useRef(false);
  useEffect(() => {
    if (!dealer) {
      setActive(1);
      return;
    }

    const selectedDealer = allDealerList.find(d => d?.value === dealerValue);
    if (!selectedDealer) {
      return;
    }

    setActive(parseInt(selectedDealer.active));
    if (!hasSyncedRef.current || selectedDealer?.value !== dealerValue) {
      handleLayoutSetDealerInfo(selectedDealer);
      hasSyncedRef.current = true;
    }
  }, [allDealerList, dealer]);

  // If active state is changed
  useEffect(() => {
    if (allDealerList && allDealerList.length > 0) {
      let filteredDealers = [];
      if (active === 1) {
        if (activeDealersList.length > 0) {
          filteredDealers = [...activeDealersList];
        } else {
          setActive(0);
        }
      }
      if (active === 0) {
        if (inActiveDealersList.length > 0) {
          filteredDealers = [...inActiveDealersList];
        } else {
          setActive(1);
        }
      }

      const sortedDealers = filteredDealers.sort(
        (a, b) => (a.isTestDealership === 1) - (b.isTestDealership === 1)
      );

      const demoOptionIndex = sortedDealers.findIndex(option => option.isTestDealership === 1);

      if (demoOptionIndex !== -1) {
        sortedDealers[demoOptionIndex] = {
          ...sortedDealers[demoOptionIndex],
          customStyle: { borderTop: `6px solid ${themeColorObj?.[isLightTheme ? 'gray-500' : 'gray-800']}` }
        };
      }
      setFilteredDealers(sortedDealers);
    }
  }, [active, allDealerList, activeDealersList, inActiveDealersList]);

  // set current dealer
  useEffect(() => {
    if (filteredDealers && filteredDealers.length > 0) {
      const params = new URLSearchParams(location.search);
      let dealerId = parseInt(Number(params.get('dealer')));
      let selectedDealer = null;
      if (dealerId) {
        selectedDealer = filteredDealers.find(item => item?.value === dealerId);
        if (selectedDealer) {
          handleLayoutSetDealerInfo(selectedDealer);
        } else {
          handleLayoutSetDealerInfo(filteredDealers[0]);
        }
      } else if (dealer) {
        selectedDealer = filteredDealers.find(item => item?.value === dealerValue);
        // Last dealer from local storage
        let lastIndex = 0;
        if (active !== dealer?.active) {
          if (active) {
            const idx = sessionStorage.getItem(STORAGE_LASTACTIVEINDEX);
            if (idx) {
              lastIndex = parseInt(idx);
            }
          } else {
            const inidx = sessionStorage.getItem(STORAGE_LASTINACTIVEINDEX);
            if (inidx) {
              lastIndex = parseInt(inidx);
            }
          }
        }

        let index = 0;
        if (!selectedDealer) {
          selectedDealer = filteredDealers[lastIndex];
        } else {
          index = filteredDealers.findIndex(opt => opt.value === dealerValue);
          if (index >= 0 && active === dealer?.active) {
            if (active) {
              sessionStorage.setItem(STORAGE_LASTACTIVEINDEX, index);
            } else {
              sessionStorage.setItem(STORAGE_LASTINACTIVEINDEX, index);
            }
          }
        }

        if (selectedDealer?.value !== dealerValue) {
          handleLayoutSetDealerInfo(selectedDealer);
        }
      } else {
        handleLayoutSetDealerInfo(filteredDealers[0]);
      }
    }
  }, [active, filteredDealers, dealerValue, location]);

  // on change of current dealer
  useEffect(() => {
    if (dealer) {
      let index = filteredDealers.findIndex(opt => opt.value === dealerValue);
      if (index >= 0) {
        setCurrentIndex(index);
      }
    }
  }, [filteredDealers, dealer]);

  return (
    <>
      <CustomSelect
        id={`dealer-${dealerValue}`}
        options={filteredDealers}
        selected={filteredDealers[currentIndex]}
        isDisabled={isLoading}
        className="select-dealership"
        containerMaxWidth="200px"
        optionsMaxHeight="70vh"
        onChange={itemdealer => {
          cancelPreviousPageRequests(location.pathname);
          if (userIsDirectlogin) {
            handleLayoutSetSessionDealerInfo(itemdealer);
          } else {
            handleLayoutSetDealerInfo(itemdealer);
          }

          const loactionPath = location?.pathname?.split('/')?.[1];
          if (loactionPath === `${INVENTORY_DETAILS_PAGE_PATH?.slice(1)}`) {
            navigate(`${INVENTORY_PAGE_PATH}`);
          }
          if (loactionPath === `${DEALERPROFILE_PAGE_PATH?.slice(1)}`) {
            navigate(`${DEALERPROFILE_PAGE_PATH}/${itemdealer?.value}`);
          }
          if (location?.pathname.split('/')[1] === `${VIN_STATUS_TRACKER.slice(1)}`) {
            if (location.pathname.split('/').length === 4) {
              const vin = location.pathname.split('/')[3];
              navigate(`${VIN_STATUS_TRACKER}/${dealer.value}/${vin}`);
            }
          }
        }}
      />

      {hasActionPermission([ACTION_ACTIVE_DEALER_IN_DD_LIST], [MODULE_APP_GLOBAL]) && (
        <>
          <span className="pl-2" id="btn-active">
            <i
              className={`fa fa-fw fa-circle ${active ? CLR_TEXT_ACTIVE : CLR_TEXT_INACTIVE}`}
              aria-hidden="true"
              onClick={() => setActive(current => (current === 1 ? 0 : 1))}
            />
          </span>
          <UncontrolledTooltip target="btn-active">
            Now showing
            {active ? (
              <span className={`${CLR_TEXT_ACTIVE} font-weight-bold`}>
                {' '}
                {filteredDealers
                  ? filteredDealers?.filter(f => f && f.isTestDealership === 0).length
                  : 0}{' '}
                active{' '}
              </span>
            ) : (
              <span className={`${CLR_TEXT_INACTIVE} font-weight-bold`}>
                {' '}
                {filteredDealers
                  ? filteredDealers?.filter(f => f && f.isTestDealership === 0).length
                  : 0}{' '}
                in-active{' '}
              </span>
            )}
            dealerships.
          </UncontrolledTooltip>
        </>
      )}
    </>
  );
};

const mapDispatchToProps = { handleDataFilter, handlePreSavedId };

DealershipsDropdown.displayName = 'DealershipsDropdown';
export default connect(null, mapDispatchToProps)(memo(DealershipsDropdown));
